import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, ScrollView, Image, Text, Keyboard, Switch } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import { toast } from 'react-hot-toast';
import * as Animatable from 'react-native-animatable';
import imageCompression from 'browser-image-compression';
import { cloneDeep } from 'lodash';

// Child Components
import ModifierGroup from './component_modifier_group';
import ModifierModal from './component_modifier_modal';

// Custom Components
import LoadingView from '../../components/component_loading_view';
import { Button, NavBar, TextBox } from '../../components';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

// APIs
import { createFoodItem, editFoodItem, deleteItem } from '../../modules';

// Utils
import { showAlert, showAppError, toTitleCase } from '../../utils/util_helpers';
import ModifierCopyModal from './component_modifier_copy_modal';

// Animatable Comps
const AnimatableScrollView = Animatable.createAnimatableComponent(ScrollView);

export function MenuItemDetails() {
    const {
        categoryData = {},
        currentFoodItemList = [],
        lastFoodItemPosition = 0,
        itemData = null,
        onFoodItemUpdated = () => {},
        onNewFoodItemAdded = () => {},
        onFoodItemRemoved = () => {}
    } = useRoute()?.params || {};

    const EDIT_MODE = !!itemData;

    // region Tag Data
    const TAG_DATA = [
        {
            name: 'Vegan',
            alias: 'vegan',
            image: require('../../images/menu_tag_vegetarian.png')
        },
        {
            name: 'Vegetarian',
            alias: 'vegetarian',
            image: require('../../images/menu_tag_vegan.png')
        },
        {
            name: 'Gluten Free',
            alias: 'gluten_free',
            image: require('../../images/menu_tag_gluten_free.png')
        },
        {
            name: 'Dairy Free',
            alias: 'dairy_free',
            image: require('../../images/menu_tag_dairy_free.png')
        },
        {
            name: 'Contains Nuts',
            alias: 'contains_nuts',
            image: require('../../images/menu_tag_nuts.png')
        }
    ];
    // endregion

    // region Refs
    const txtName = useRef(null);
    const txtPrice = useRef(null);
    const txtDescription = useRef(null);
    const modifierModal = useRef(null);
    const modifierCopyModal = useRef(null);
    const photoPicker = useRef(null);
    // endregion

    // region Global State
    const [truckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [locationList] = useGlobalState(STATE_KEYS.LOCATION_LIST);
    // endregion

    // region Local State
    const [isLoading, setLoading] = useState(false);
    const [compShouldLoad, setCompShouldLoad] = useState(false);
    const [hasSquare, setHasSquare] = useState(false);
    const [defaultMenuStatus, setDefaultMenuStatus] = useState([]);
    const [isMenuStatusMissing, setMenuStatusMissing] = useState(false);

    const [itemName, setItemName] = useState('');
    const [itemPrice, setItemPrice] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [foodPhoto, setFoodPhoto] = useState('');
    const [foodPhotoFile, setFoodPhotoFile] = useState(null);
    const [modifierList, setModifierList] = useState([]);
    const [specialTagList, setSpecialTagList] = useState([]);
    const [status, setStatus] = useState(1);
    const [menuStatus, setMenuStatus] = useState([]);

    const [isDeleting, setDeleting] = useState(false);
    // endregion

    // region Function - Check Name Exists
    const checkNameExists = (newName) => (
        !!currentFoodItemList.find((item) => item.name.toLowerCase().trim() === newName.toLowerCase().trim())
    )
    // endregion

    // region INITIALIZE DATA
    const initializeData = () => {
        const hasSquareValue = locationList.length > 0;
        setHasSquare(hasSquareValue);
        const defaultMenuStatusValue = hasSquareValue ? locationList.map((location) => ({ status: 1, squareup_location_id: location['squareup_location_id'] })) : [];
        setDefaultMenuStatus(defaultMenuStatusValue);
        const menuStatusMissingValue = (!EDIT_MODE || !hasSquareValue) ? false : (!itemData['menu_status'] ? true : (itemData['menu_status'].length !== locationList.length));
        setMenuStatusMissing(menuStatusMissingValue);

        // Get initial tag list
        let initialTagList;
        if (EDIT_MODE) {
            initialTagList = TAG_DATA
                .map((item) => (itemData.hasOwnProperty(item.alias) && (itemData[item.alias].toString() === '1')) ? item : '')
                .filter((item) => item !== '');
        }
        else initialTagList = [];

        // Load item data
        setItemName(itemData?.name || '');
        setItemPrice(itemData?.price || '');
        setItemDescription(itemData?.description || '');
        setFoodPhoto(itemData?.['photo_webp'] || itemData?.photo || '');
        setModifierList(itemData?.modifiers || []);
        setSpecialTagList(initialTagList);
        setStatus(itemData?.status || 1);
        setMenuStatus(EDIT_MODE ? (menuStatusMissingValue ? defaultMenuStatusValue : (itemData['menu_status'] || [])) : defaultMenuStatusValue);

        // Load components
        setTimeout(() => setCompShouldLoad(true), 1000);
    }
    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => initializeData(), 500);
    }, []);
    // endregion

    // region Function - Has Changes
    const hasChanges = () => {
        if (itemData) {
            // Compare special tags
            const oldTags = TAG_DATA.map((item) => (itemData && itemData.hasOwnProperty(item.alias) && itemData[item.alias].toString() === '1') ? item : '')
                .filter((item) => item !== '')
                .map((item) => item.alias)
                .join(',');
            const newTags = specialTagList.map((item) => item.alias).join(',');
            const hasTagChange = oldTags.localeCompare(newTags) !== 0;

            // Compare Status
            const oldStatusList = locationList.length > 1 ? (isMenuStatusMissing ? defaultMenuStatus : itemData['menu_status'])?.map((item) => item.status).join(',') : itemData['status']?.toString();
            const newStatusList = locationList.length > 1 ? menuStatus.map((item) => item.status).join(',') : status?.toString();
            const hasStatusChange = oldStatusList?.localeCompare(newStatusList) !== 0;

            // Compare Food Photo
            const hasFoodPhotoChange = !!foodPhoto?.trim() && !foodPhoto.startsWith('http');

            // Compare Others
            const hasNameChange = itemData['name']?.localeCompare(itemName) !== 0;
            const hasPriceChange = itemData['price']?.localeCompare(itemPrice) !== 0;
            const hasDescriptionChange = itemData['description']?.localeCompare(itemDescription) !== 0;

            // Return data
            return(
                hasTagChange ||
                hasStatusChange ||
                hasFoodPhotoChange ||
                hasNameChange ||
                hasPriceChange ||
                hasDescriptionChange
            );
        }
        else return true;
    }
    // endregion

    // region Function - Save Item
    const goSaveItem = () => {
        Keyboard.dismiss();

        if (!itemName?.trim() || !itemDescription?.trim() || !itemPrice?.trim()) {
            showAlert({ text: 'Item name, price, and description are required' });
            return;
        }

        if (checkNameExists(itemName) && !EDIT_MODE) {
            showAlert({ text: 'Item name already existed in this category, please select a different name' });
            return;
        }

        if (Number(itemPrice) < 0) {
            showAlert({ text: 'Item price cannot be negative' });
            return;
        }

        setLoading(true);
        const sendingItemData = {
            id: EDIT_MODE ? itemData?.id : -1,
            name: itemName,
            description: itemDescription,
            price: itemPrice.toString().replace(',', '.'), // US decimal format
            category_id: categoryData ? categoryData.id : itemData.category.id,
            gluten_free: specialTagList.find((item) => item.name === 'Gluten Free') ? 1 : 0,
            vegan: specialTagList.find((item) => item.name === 'Vegan') ? 1 : 0,
            vegetarian: specialTagList.find((item) => item.name === 'Vegetarian') ? 1 : 0,
            dairy_free: specialTagList.find((item) => item.name === 'Dairy Free') ? 1 : 0,
            contains_nuts: specialTagList.find((item) => item.name === 'Contains Nuts') ? 1 : 0,
            status: status,
            menu_status: menuStatus?.map((item) => Object.assign(cloneDeep(item), { status: Number(item['status']) })) || [],
            order: lastFoodItemPosition || itemData?.order || 1
        };

        // Main function
        const mainFunc = () => {
            // Only add field "modifiers" when there's at least one group
            if (modifierList && Array.isArray(modifierList) && modifierList.length > 0) {
                sendingItemData['modifiers'] = modifierList;
            }

            const apiCall = EDIT_MODE ?
                editFoodItem(truckData.id, categoryData.id, sendingItemData)
                :
                createFoodItem(truckData.id, categoryData.id, sendingItemData);

            apiCall
                .then((result) => {
                    if (EDIT_MODE) {
                        onFoodItemUpdated(Object.assign(cloneDeep(itemData), result));
                    }
                    else onNewFoodItemAdded(Object.assign(cloneDeep(result || {}), { key: `foodItem_${result.id}`, isNew: true }), categoryData.id);

                    // Close the scene
                    RootNavigation.goBack();
                })
                .catch((error) => showAppError(error))
                .finally(() => setLoading(false));
        };

        // Add food photo for create/update
        if (foodPhoto && !foodPhoto.startsWith('http')) {
            // Compress photo
            const imageCompressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
            imageCompression(foodPhotoFile, imageCompressionOptions)
                .then((compressedPhoto) => {
                    // Append photo to sending data
                    sendingItemData['photo'] = compressedPhoto;

                    // Call function
                    mainFunc();
                })
                .catch((error) => {
                    setLoading(false);
                    showAppError(error.message);
                });
        }
        else mainFunc();
    }
    // endregion

    // region Function - Delete Item
    const goDeleteItem = () => {
        if (isLoading || isDeleting) return;
        showAlert({
            text: `Are you sure you want to delete this food item? This action cannot be undone.`,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    setDeleting(true);
                    setTimeout(() => {
                        deleteItem(truckData.id, categoryData.id, itemData.id)
                            .then(() => {
                                // Remove from parent scene
                                onFoodItemRemoved(itemData.id, categoryData.id);

                                // Go back
                                RootNavigation.goBack();

                                // Notify user
                                setTimeout(() => toast(`"${itemData.name}" has been removed from the category "${categoryData.name}"`), 1000);
                            })
                            .catch((error) => showAppError(error))
                            .finally(() => setDeleting(false));
                    }, 2000);
                }
            }
        })
    }
    // endregion

    // region Function - On New Modifier Pressed
    const _onNewModifierPressed = () => {
        showAlert({
            text: 'Do you want to create a new modifier group from scratch or copy existing ones from other food items?',
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Create New',
                onPress: () => modifierModal.current?.showModal(null)
            },
            thirdButton: {
                text: 'Copy Existing',
                onPress: () => modifierCopyModal.current?.showModal()
            }
        })
    }
    // endregion

    // region Render Food Photo
    const logoSize = 160;
    const renderFoodPhoto = () => (
        <TouchableOpacity onPress={() => photoPicker.current?.click()}>
            {/* Photo */}
            <View style={{ width: logoSize, height: logoSize, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, overflow: 'hidden' }}>
                <Image
                    source={foodPhoto ? { uri: foodPhoto } : require('../../images/illustration-empty-food.png')}
                    style={{ width: logoSize, height: logoSize }}
                    resizeMode={'cover'}
                />
            </View>

            {/* Picker */}
            <input
                id={'logoPicker'}
                type={'file'}
                accept={'.png, .jpg, .jpeg'}
                ref={photoPicker}
                style={{ display: 'none' }}
                onChange={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    const file = event.target.files[0];
                    if (file) {
                        setFoodPhotoFile(file);
                        setFoodPhoto(URL.createObjectURL(file));
                    }
                }}
            />
        </TouchableOpacity>
    )
    // endregion

    // region Render Info View
    const renderInfoView = () => (
        <View style={{ alignSelf: 'stretch', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, paddingHorizontal: 24, marginTop: 24 }}>
            {/* Name, Price and Picture */}
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 24, width: '100%' }}>
                {/* Name and Price */}
                <View style={{ flex: 1, paddingRight: 24 }}>
                    {/* Name */}
                    <TextBox
                        ref={txtName}
                        onChangeText={(text) => setItemName(text)}
                        onSubmitEditing={() => txtPrice.current?.focus()}
                        blurOnSubmit={true}
                        autoCapitalize={'words'}
                        value={itemName}
                        title={'Item Name'}
                        placeholder={'Item Name'}
                        style={{ marginTop: 0 }}
                    />

                    {/* Price */}
                    <TextBox
                        ref={txtPrice}
                        returnKeyType={'done'}
                        keyboardType={'decimal-pad'}
                        onChangeText={(text) => setItemPrice(text.replace('-', ''))}
                        onSubmitEditing={() => txtDescription.current?.focus()}
                        blurOnSubmit={true}
                        value={itemPrice}
                        title={'Item Price'}
                        placeholder={'0.00'}
                        prefix={'$'}
                    />
                </View>

                {/* Photo Selector */}
                {renderFoodPhoto()}
            </View>

            {/* Description */}
            <View style={{ width: '100%', paddingBottom: 16, marginTop: 16 }}>
                <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 20, color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY }}>
                    Description
                </Text>

                <TextBox
                    ref={txtDescription}
                    contentBoxStyle={{ height: 150 }}
                    multiline={true}
                    returnKeyType={'done'}
                    onChangeText={(text) => setItemDescription(text)}
                    value={itemDescription}
                    title={'Description'}
                    placeholder={'Say something about this food item...'}
                />
            </View>

            {/* Special Tags */}
            <View style={{ width: '100%', paddingVertical: 24 }}>
                <Text style={{ flex: 1, fontWeight: 'bold', fontSize: 20, color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, marginBottom: 8 }}>
                    Special Tags
                </Text>

                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignSelf: 'stretch' }}>
                    {
                        TAG_DATA.map((item, index) => {
                            const isChosen = specialTagList.find((tag) => tag.alias === item.alias);
                            return(
                                <TouchableOpacity
                                    key={`tagItem_${item.alias}`}
                                    onPress={() => {
                                        if (isChosen) {
                                            setSpecialTagList(specialTagList.filter((tag) => tag.alias !== item.alias));
                                        }
                                        else setSpecialTagList(specialTagList.concat(item));
                                    }}
                                    style={{
                                        flexDirection: 'row', alignItems: 'center',
                                        paddingHorizontal: 16, paddingVertical: 12, borderRadius: 24,
                                        backgroundColor: isChosen ? 'black' : GLOBAL.COLOR.BACKGROUND_GREY,
                                        marginTop: 8, marginRight: 8
                                    }}>
                                    <Image
                                        source={item.image}
                                        style={{ width: 24, height: 24, tintColor: isChosen ? 'white' : item.color }}
                                        resizeMode={'contain'}
                                    />

                                    <Text style={{ fontWeight: '500', color: isChosen ? 'white' : 'black', marginLeft: 8, fontSize: 16 }}>
                                        {item.name}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })
                    }
                </View>
            </View>
        </View>
    )
    // endregion

    // region Render Status View
    const renderStatusView = () => {
        const hasMultipleSquare = locationList.length > 1;
        return(
            <View style={{
                alignSelf: 'stretch', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER,
                padding: 32, paddingBottom: 24, marginTop: 24
            }}>
                <Text style={{ flex: 1, fontWeight: '900', fontSize: 22, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                    {hasMultipleSquare ? 'Active Status by Location' : 'Active Status'}
                </Text>

                {/* Toggles */}
                {
                    hasMultipleSquare ?
                        <View style={{ width: '100%', marginTop: 16 }}>
                            {
                                menuStatus?.map((item, index) => (
                                    <View
                                        key={`locationItem_${item['squareup_location_id']}`}
                                        style={{ width: '100%', flexDirection: 'row', alignItems: 'center', height: 56 }}>
                                        <Text style={{ flex: 1, fontWeight: '400', fontSize: 18, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                                            {locationList.find((location) => item['squareup_location_id'] === location['squareup_location_id'])?.name || '(Square Location)'}
                                        </Text>

                                        <Switch
                                            value={Number(item.status) === 1}
                                            trackColor={'#DEDEDE'}
                                            activeTrackColor={GLOBAL.COLOR.BASIC.UI_GREEN}
                                            activeThumbColor={'white'}
                                            thumbColor={'white'}
                                            onValueChange={(switchValue) => {
                                                const tempArr = cloneDeep(menuStatus);
                                                tempArr[index].status = (switchValue ? 1 : 0);
                                                setMenuStatus(tempArr)
                                            }}
                                        />
                                    </View>
                                ))
                            }
                        </View>
                        :
                        <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', height: 56, marginTop: 16 }}>
                            <Text style={{ flex: 1, fontWeight: '400', fontSize: 18, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                                Food item is active
                            </Text>

                            <Switch
                                value={hasSquare ? (menuStatus[0]?.status === 1) : (status === 1)}
                                trackColor={'#DEDEDE'}
                                activeTrackColor={GLOBAL.COLOR.BASIC.UI_GREEN}
                                activeThumbColor={'white'}
                                thumbColor={'white'}
                                onValueChange={(switchValue) => {
                                    if (hasSquare) {
                                        const tempArr = cloneDeep((menuStatus && !isMenuStatusMissing) ? menuStatus : defaultMenuStatus);
                                        tempArr[0].status = (switchValue ? 1 : 0);
                                        setMenuStatus(tempArr)
                                    }
                                    else setStatus(switchValue ? 1 : 0);
                                }}
                            />
                        </View>
                }
            </View>
        );
    }
    // endregion

    // region Render Modifier View
    const renderModifierView = () => {
        return(
            <View style={{ alignSelf: 'stretch', backgroundColor: 'white', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, padding: 32, marginTop: 24 }}>
                {/* Title */}
                <Text style={{ flex: 1, fontWeight: '900', fontSize: 22, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                    Modifiers
                </Text>

                {/* Description */}
                <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, fontSize: 16, fontWeight: 'normal', marginTop: 12, alignSelf: 'stretch' }}>
                    Modifiers give your customers the ability to customize their orders the way they want!
                </Text>

                {/* List of Modifiers */}
                <View style={{ backgroundColor: 'white', width: '100%', marginTop: 16 }}>
                    {/* List */}
                    {modifierList.map((modifierGroup, index) => (
                        <ModifierGroup
                            key={`modifierGroup_${modifierGroup.id || index}`}
                            customStyle={{ marginTop: 24, paddingBottom: 24, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}
                            modifierGroupData={modifierGroup}
                            modifierGroupIndex={index}
                            onModifierGroupPressed={() => modifierModal.current?.showModal(modifierGroup)}
                        />
                    ))}

                    {/* New Modifier Group Button */}
                    <TouchableOpacity
                        onPress={_onNewModifierPressed}
                        style={{ paddingVertical: 8, flexDirection: 'row', alignItems: 'center', marginTop: modifierList.length > 0 ? 24 : 8 }}>
                        <Image
                            source={require('../../images/iconLinePlus@3x.png')}
                            style={{ width: 24, height: 24, tintColor: GLOBAL.COLOR.BASIC.UI_BLUE }}
                        />

                        <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.UI_BLUE, marginLeft: 12, fontSize: 18 }}>
                            New Modifier Group
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }
    // endregion

    // region Render Modifier Modal
    const renderModifierModal = () => (
        <ModifierModal
            ref={modifierModal}
            truckData={truckData}
            foodItemData={itemData}
            modifierGroupNameList={modifierList.map((group) => ({ id: group.id, name: group.name.trim().toLowerCase() }))}
            categoryData={categoryData}
            onModifierGroupCreated={(newGroupData, tempOnly = false) => {
                const newModifierList = modifierList.concat(newGroupData);
                setModifierList(newModifierList);
                modifierModal.current?.hideModal();

                // Update <ProfileMenu/>
                if (!tempOnly) onFoodItemUpdated(Object.assign(cloneDeep(itemData), { modifiers: newModifierList }));
            }}
            onModifierGroupUpdated={(updatedGroupData, tempOnly = false) => {
                const oldItemIndex = modifierList.findIndex((item) => item.id === updatedGroupData.id);
                if (oldItemIndex > -1) {
                    const tempArr = cloneDeep(modifierList);
                    tempArr.splice(oldItemIndex, 1, updatedGroupData);
                    setModifierList(tempArr);

                    // Update <ProfileMenu/>
                    if (!tempOnly) onFoodItemUpdated(Object.assign(cloneDeep(itemData), { modifiers: tempArr }));
                }
                modifierModal.current?.hideModal();
            }}
            onModifierGroupRemoved={(modifierGroupID, tempOnly = false) => {
                const oldItemIndex = modifierList.findIndex((item) => item.id === modifierGroupID);
                if (oldItemIndex > -1) {
                    const tempArr = cloneDeep(modifierList);
                    tempArr.splice(oldItemIndex, 1);
                    setModifierList(tempArr);

                    // Update <ProfileMenu/>
                    if (!tempOnly) onFoodItemUpdated(Object.assign(cloneDeep(itemData), { modifiers: tempArr }));
                }
                modifierModal.current?.hideModal();
            }}
        />
    )
    // endregion

    // region Render Modifier Copy Modal
    const renderModifierCopyModal = () => (
        <ModifierCopyModal
            ref={modifierCopyModal}
            foodItemData={itemData}
            categoryData={categoryData}
            truckData={truckData}
            modifierGroupNameList={modifierList.map((group) => group.name.trim().toLowerCase())}
            onModifierGroupsImported={(newGroupDataArr, tempOnly = false) => {
                const tempArr = cloneDeep(newGroupDataArr);

                // If this is a new food item, delete all modifier items' IDs
                if (!EDIT_MODE) {
                    tempArr.forEach((modifierGroup) => modifierGroup.items.forEach((modifierItem) => (delete modifierItem.id)));
                }

                // Save to state
                const newModifierList = modifierList.concat(tempArr);
                setModifierList(newModifierList);

                // Close modal
                modifierCopyModal.current?.closeModal();

                // Update ProfileMenu as well
                if (!tempOnly && EDIT_MODE) onFoodItemUpdated(Object.assign(cloneDeep(itemData), { modifiers: cloneDeep(newModifierList) }));
            }}
        />
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ flex: 1, width: '100%' }}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => {
                    if (hasChanges()) {
                        showAlert({
                            text: 'You have made changes that are not yet saved, are you sure you want to continue?',
                            confirmButton: {
                                text: 'Discard Changes',
                                color: 'red',
                                onPress: () => RootNavigation.goBack()
                            },
                            cancelButton: { text: 'Stay Here' }
                        })
                    }
                    else RootNavigation.goBack();
                }}
                title={`${itemData ? 'Edit' : 'New'} Food Item`}
                subTitle={itemData ? `${itemData?.name} | ${categoryData?.name}` : `Category: "${toTitleCase(categoryData?.name)}"`}
                leftButtonIconName={'close'}
                rightButtonTitle={(isLoading || isDeleting || !compShouldLoad || !hasChanges()) ? null : (EDIT_MODE ? 'Save Item' : 'Add Item')}
                rightButtonOnPress={() => goSaveItem()}
            />

            {/* Main Content */}
            {
                compShouldLoad ?
                    <AnimatableScrollView
                        animation={'fadeIn'}
                        style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                        contentContainerStyle={{ alignItems: 'center', paddingBottom: 128, paddingHorizontal: '20%' }}>
                        <View style={{ flex: 1, width: '100%' }}>
                            {/* Info View */}
                            {renderInfoView()}

                            {/* Status View */}
                            {renderStatusView()}

                            {/* Modifiers */}
                            {renderModifierView()}

                            {/* Cover View */}
                            {isDeleting && <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)' }} />}
                        </View>

                        {/* Delete Button */}
                        {
                            EDIT_MODE &&
                            <Button
                                text={'Delete Food Item'}
                                textColor={'white'}
                                onPress={() => goDeleteItem()}
                                style={{ alignSelf: 'stretch', backgroundColor: GLOBAL.COLOR.MAIN, marginTop: 24 }}
                                isLoading={isDeleting}
                                loadingText={'Deleting Food Item'}
                            />
                        }
                    </AnimatableScrollView>
                    :
                    <LoadingView isLoading={true} style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}/>
            }

            {/* Modals */}
            {
                compShouldLoad &&
                <>
                    {/* Modifier Modal */}
                    {renderModifierModal()}

                    {/* Modifier Copy Modal */}
                    {renderModifierCopyModal()}
                </>
            }

            {/* Loading View */}
            {isLoading && <LoadingView isLoading={true} isAbsolute={true} description={EDIT_MODE ? 'Saving Item...' : 'Creating Item...'} />}
        </View>
    );
    // endregion
}
